import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Feather, Wind, Globe, Leaf, Quote, Star, Instagram, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FadeIn } from "@/components/FadeIn";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const CATEGORY_META = [
  { label: "Solid Modal", slug: "solid-modal" },
  { label: "Print Modal", slug: "print-modal" },
  { label: "Chiffon", slug: "chiffon" },
];

export const SectionHeading = ({ eyebrow, title, cta, ctaTo }) => (
  <div className="flex items-end justify-between mb-12 md:mb-16">
    <div>
      {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
      <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">{title}</h2>
    </div>
    {cta && (
      <Link to={ctaTo} className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.2em] nav-underline pb-1">
        {cta} <ArrowRight strokeWidth={1} className="h-4 w-4" />
      </Link>
    )}
  </div>
);

export const FeaturedCategories = () => {
  const { categories } = useSiteSettings();
  return (
    <section className="container-lira py-24 md:py-32" data-testid="featured-categories">
      <FadeIn>
        <div className="text-center mb-16">
          <p className="eyebrow mb-3">Shop by Fabric</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">Explore the Collections</h2>
        </div>
      </FadeIn>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {CATEGORY_META.map((c, i) => (
          <FadeIn key={c.slug} delay={i * 0.1}>
            <Link to={`/collection/${c.slug}`} className="group block" data-testid={`category-card-${c.slug}`}>
              <div className="overflow-hidden aspect-[4/5] rounded-sm bg-beige">
                <img src={categories?.[c.slug]} alt={c.label} className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
              </div>
              <div className="flex items-center justify-between mt-5">
                <h3 className="font-serif text-2xl">{c.label}</h3>
                <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] group-hover:text-[#1A1A1A] transition-colors">
                  Shop Now <ArrowRight strokeWidth={1} className="h-4 w-4 -translate-x-1 group-hover:translate-x-0 transition-transform" />
                </span>
              </div>
            </Link>
          </FadeIn>
        ))}
      </div>
    </section>
  );
};

export const LifestyleBanner = () => {
  const { lifestyle_banner } = useSiteSettings();
  return (
    <section className="relative h-[70vh] min-h-[500px] overflow-hidden" data-testid="lifestyle-banner">
      <img
        src={lifestyle_banner}
        alt="LIRA editorial campaign" className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/25" />
      <div className="relative h-full container-lira flex flex-col items-center justify-center text-center text-white">
        <FadeIn>
          <p className="text-xs uppercase tracking-[0.3em] mb-5">The Signature Series</p>
          <h2 className="font-serif text-4xl md:text-6xl font-light max-w-2xl leading-tight">Effortless elegance, made to last</h2>
          <div className="mt-10">
            <Link to="/collection/all" className="inline-flex items-center justify-center rounded-full bg-white text-[#1A1A1A] px-9 py-3.5 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-500 hover:scale-[1.03]">
              Discover the Edit
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

const WHY = [
  { icon: Feather, title: "Premium Modal Fabric", text: "Woven from the finest modal for a soft, matte, luxurious finish." },
  { icon: Wind, title: "Lightweight & Breathable", text: "Airy, non-slip drape that stays comfortable from dawn to dusk." },
  { icon: Globe, title: "Worldwide Shipping", text: "Delivered beautifully wrapped to over 60 countries." },
  { icon: Leaf, title: "Ethically Made", text: "Responsibly sourced and crafted in small, fair-wage ateliers." },
];

export const WhyChooseUs = () => (
  <section className="bg-beige py-24 md:py-32" data-testid="why-choose-us">
    <div className="container-lira">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
        {WHY.map((w, i) => (
          <FadeIn key={w.title} delay={i * 0.1} className="flex flex-col items-center text-center gap-4">
            <w.icon strokeWidth={1} className="h-8 w-8" />
            <h3 className="font-serif text-xl">{w.title}</h3>
            <p className="text-sm text-[#5a5a5a] leading-relaxed max-w-[220px]">{w.text}</p>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

export const ReviewsSlider = () => {
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/global-reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  if (reviews.length === 0) return null;
  const r = reviews[idx];

  return (
    <section className="container-lira py-24 md:py-32" data-testid="reviews-slider">
      <div className="max-w-3xl mx-auto text-center">
        <Quote strokeWidth={1} className="h-12 w-12 mx-auto text-sand mb-8" />
        <FadeIn key={idx}>
          <div className="flex items-center justify-center gap-1 mb-6">
            {Array.from({ length: r.rating }).map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-[#1A1A1A] text-[#1A1A1A]" />
            ))}
          </div>
          <p className="font-serif text-2xl md:text-3xl font-light leading-relaxed">"{r.quote}"</p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <img src={r.avatar} alt={r.name} className="h-12 w-12 rounded-full object-cover" />
            <div className="text-left">
              <p className="text-sm font-medium">{r.name}</p>
              <p className="text-xs text-[#737373]">{r.location}</p>
            </div>
          </div>
        </FadeIn>
        <div className="mt-12 flex items-center justify-center gap-4">
          <button onClick={() => setIdx((i) => (i - 1 + reviews.length) % reviews.length)} aria-label="Previous review" className="h-10 w-10 grid place-items-center rounded-full border border-[#E5E5E5] hover:bg-[#1A1A1A] hover:text-white transition-colors">
            <ChevronLeft strokeWidth={1} className="h-4 w-4" />
          </button>
          <button onClick={() => setIdx((i) => (i + 1) % reviews.length)} aria-label="Next review" className="h-10 w-10 grid place-items-center rounded-full border border-[#E5E5E5] hover:bg-[#1A1A1A] hover:text-white transition-colors">
            <ChevronRight strokeWidth={1} className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export const Community = () => {
  const [data, setData] = useState({ images: [], handle: "@Lirascarves" });
  useEffect(() => {
    api.get("/community").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <section className="py-24 md:py-32" data-testid="community-section">
      <div className="container-lira text-center mb-14">
        <p className="eyebrow mb-3">Our Community</p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">{data.handle}</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
        {data.images.map((src, i) => (
          <a key={i} href="https://instagram.com" target="_blank" rel="noreferrer" className="group relative aspect-square overflow-hidden bg-beige">
            <img src={src} alt="LIRA community" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 grid place-items-center">
              <Instagram strokeWidth={1} className="h-7 w-7 text-white" />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
};

export const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to LIRA.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    }
  };
  return (
    <section className="bg-beige py-24 md:py-32" data-testid="newsletter-section">
      <div className="container-lira max-w-2xl mx-auto text-center">
        <FadeIn>
          <p className="eyebrow mb-3">Newsletter</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">Join Our Community</h2>
          <p className="mt-5 text-[#5a5a5a] leading-relaxed">
            Sign up for early access to new collections, private sales, and styling inspiration — thoughtfully curated, never noisy.
          </p>
          <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto">
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full flex-1 bg-white border border-[#E5E5E5] rounded-full px-6 py-3.5 text-sm outline-none focus:ring-1 focus:ring-black"
              data-testid="newsletter-input"
            />
            <button type="submit" className="btn-primary w-full sm:w-auto" data-testid="newsletter-submit">Subscribe</button>
          </form>
        </FadeIn>
      </div>
    </section>
  );
};
